<script setup lang="ts">
    import {
        computed,
        nextTick,
        onBeforeUnmount,
        onMounted,
        onUnmounted,
        ref,
        useTemplateRef,
        watch,
    } from 'vue'
    import { type GlowTints, type RgbChannels, resolveGlowTints } from './utils'

    /**
     * Ambient animated "aurora" rendered with a raw WebGL fragment shader
     * (simplex noise drifting between the two tints of `resolveGlowTints`).
     * No dependencies; falls back to CSS blobs when WebGL is unavailable.
     * Without `mainColor` the color comes from the CSS cascade: set `color`
     * on the host.
     */

    const props = defineProps<{
        /** CSS selector (within the positioned parent) of the element the glow radiates from; defaults to the positioned parent box itself */
        sourceSelector?: string
        /** The agent's main color; without it, the inherited `color` */
        mainColor?: string
        /** One tone or two, as the agent sets them */
        tones?: 'auto' | 'one' | 'two'
        /** The second tone the agent chose; worked out from the first otherwise */
        secondColor?: string
    }>()

    const rootEl = useTemplateRef<HTMLElement>('rootEl')
    const canvasEl = useTemplateRef<HTMLCanvasElement>('canvasEl')
    const failed = ref(false)
    const tints = ref<GlowTints | null>(null)

    // The CSS fallback draws the same tints, when they could be read
    const tintStyle = computed(() =>
        tints.value
            ? {
                  '--pk-aurora-tint': `rgb(${tints.value.first.join(' ')})`,
                  '--pk-aurora-tint-2': `rgb(${(tints.value.duo ? tints.value.second : tints.value.first).join(' ')})`,
              }
            : undefined,
    )

    const VERTEX_SHADER = `
attribute vec2 a_position;
void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
}`

    // snoise: 2D simplex noise by Ian McEwan / Stefan Gustavson (MIT)
    const FRAGMENT_SHADER = `
precision mediump float;
uniform vec2 u_resolution;
uniform float u_time;
uniform vec3 u_color;
uniform vec3 u_color2;
uniform float u_duo;
uniform vec2 u_center;
uniform vec2 u_half;
uniform float u_spread;

vec3 permute(vec3 x) { return mod(((x * 34.0) + 1.0) * x, 289.0); }

float snoise(vec2 v) {
    const vec4 C = vec4(0.211324865405187, 0.366025403784439,
        -0.577350269189626, 0.024390243902439);
    vec2 i = floor(v + dot(v, C.yy));
    vec2 x0 = v - i + dot(i, C.xx);
    vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
    vec4 x12 = x0.xyxy + C.xxzz;
    x12.xy -= i1;
    i = mod(i, 289.0);
    vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0))
        + i.x + vec3(0.0, i1.x, 1.0));
    vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy),
        dot(x12.zw, x12.zw)), 0.0);
    m = m * m;
    m = m * m;
    vec3 x = 2.0 * fract(p * C.www) - 1.0;
    vec3 h = abs(x) - 0.5;
    vec3 ox = floor(x + 0.5);
    vec3 a0 = x - ox;
    m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
    vec3 g;
    g.x = a0.x * x0.x + h.x * x0.y;
    g.yz = a0.yz * x12.xz + h.yz * x12.yw;
    return 130.0 * dot(m, g);
}

void main() {
    vec2 px = gl_FragCoord.xy;
    vec2 rel = px - u_center;

    // Ignition: the ellipse lights up expanding from the center on mount
    float intro = smoothstep(0.0, 1.4, u_time);

    // Breathing: size and brightness oscillate gently but visibly
    float breath = 1.0
        + 0.12 * sin(u_time * 0.55)
        + 0.06 * snoise(vec2(u_time * 0.1, 3.7));
    float pulse = 1.0 + 0.2 * sin(u_time * 0.55 + 1.3);

    // Keep it simple (Gemini-style): one huge soft elliptical glow
    // centered on the pill
    vec2 radii = vec2(u_half.x * 0.95, u_half.y * 6.5)
        * u_spread
        * breath
        * mix(0.15, 1.0, intro);
    float r = length(rel / max(radii, vec2(1.0)));
    float glow = exp(-r * r * 2.2);

    // Never cut hard against the canvas bounds
    vec2 m = min(px, u_resolution - px);
    glow *= smoothstep(0.0, 32.0, min(m.x, m.y));

    // Dither: breaks the 8-bit banding rings of the very soft gradient
    // (no CSS blur on the canvas, the noise must fully mask the bands)
    glow = max(glow + (snoise(px * 0.9) - 0.5) * 0.03, 0.0);

    float alpha = glow * 0.2 * pulse * intro;

    // Two tones: left to right the glow passes from the first to the
    // second, along a border a slow noise field keeps bending
    vec2 q = rel / max(radii, vec2(1.0));
    float bands = q.x * 0.9
        + 0.7 * snoise(q * 0.8 + vec2(u_time * 0.07, -u_time * 0.05));
    vec3 color = mix(u_color, u_color2, u_duo * smoothstep(-0.6, 0.6, bands));

    // Premultiplied alpha (default canvas compositing)
    gl_FragColor = vec4(color * alpha, alpha);
}`

    let gl: WebGLRenderingContext | null = null
    let rafId = 0
    let resizeObserver: ResizeObserver | null = null
    let timeLocation: WebGLUniformLocation | null = null
    let resolutionLocation: WebGLUniformLocation | null = null
    let centerLocation: WebGLUniformLocation | null = null
    let halfLocation: WebGLUniformLocation | null = null
    let spreadLocation: WebGLUniformLocation | null = null
    let colorLocation: WebGLUniformLocation | null = null
    let color2Location: WebGLUniformLocation | null = null
    let duoLocation: WebGLUniformLocation | null = null
    const startTime = performance.now()

    const compile = (type: number, source: string): WebGLShader | null => {
        if (!gl) {
            return null
        }
        const shader = gl.createShader(type)
        if (!shader) {
            return null
        }
        gl.shaderSource(shader, source)
        gl.compileShader(shader)
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            return null
        }
        return shader
    }

    const toUnit = (channels: RgbChannels) =>
        channels.map((value) => value / 255) as [number, number, number]

    // Reads the tints where the glow sits and hands them to the shader (the
    // CSS fallback reads them from `tintStyle`)
    const applyTints = () => {
        if (!rootEl.value) {
            return
        }
        tints.value = resolveGlowTints(rootEl.value, {
            mainColor: props.mainColor,
            tones: props.tones,
            secondColor: props.secondColor,
        })
        if (!gl || failed.value || !tints.value) {
            return
        }
        gl.uniform3f(colorLocation, ...toUnit(tints.value.first))
        gl.uniform3f(color2Location, ...toUnit(tints.value.second))
        gl.uniform1f(duoLocation, tints.value.duo ? 1 : 0)
        drawFrame(prefersReducedMotion() ? 10 : undefined)
    }

    // The settings panel changes them while the preview shows the glow
    watch(
        [() => props.mainColor, () => props.tones, () => props.secondColor],
        applyTints,
    )

    const resize = () => {
        if (!gl || !canvasEl.value) {
            return
        }
        // The glow is blurred anyway: cap the DPR to keep the GPU cost low
        const dpr = Math.min(window.devicePixelRatio || 1, 1.5)
        const { clientWidth, clientHeight } = canvasEl.value
        canvasEl.value.width = Math.max(1, Math.round(clientWidth * dpr))
        canvasEl.value.height = Math.max(1, Math.round(clientHeight * dpr))
        gl.viewport(0, 0, canvasEl.value.width, canvasEl.value.height)

        // The glow radiates from the source element (by selector within the
        // positioned parent, or the parent box itself): measure it into
        // shader pixel coordinates (gl origin is bottom-left)
        const host = canvasEl.value.parentElement?.offsetParent
        const source =
            (props.sourceSelector
                ? host?.querySelector(props.sourceSelector)
                : host) ?? host
        const canvasRect = canvasEl.value.getBoundingClientRect()
        const sourceRect = source?.getBoundingClientRect()
        if (sourceRect && canvasRect.width > 0) {
            const scaleX = canvasEl.value.width / canvasRect.width
            const scaleY = canvasEl.value.height / canvasRect.height
            const centerX =
                (sourceRect.left + sourceRect.width / 2 - canvasRect.left) *
                scaleX
            const centerY =
                (canvasRect.bottom - (sourceRect.top + sourceRect.height / 2)) *
                scaleY
            const halfWidth = (sourceRect.width / 2) * scaleX
            const halfHeight = (sourceRect.height / 2) * scaleY
            gl.uniform2f(centerLocation, centerX, centerY)
            gl.uniform2f(halfLocation, halfWidth, halfHeight)
            // Optional responsive boost of the glow size (e.g. mobile)
            const spread = Number.parseFloat(
                getComputedStyle(canvasEl.value).getPropertyValue(
                    '--aurora-spread',
                ),
            )
            gl.uniform1f(spreadLocation, Number.isNaN(spread) ? 1 : spread)
        }

        // Redraw before the next paint: setting the canvas size reallocates
        // the drawing buffer, and presenting it undrawn can flash as an
        // uninitialized (white) frame. With reduced motion (no loop) this
        // also keeps the settled static frame across resizes.
        drawFrame(prefersReducedMotion() ? 10 : undefined)
    }

    const prefersReducedMotion = () =>
        window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const drawFrame = (timeSeconds?: number) => {
        if (!gl || !canvasEl.value) {
            return
        }
        gl.uniform1f(
            timeLocation,
            timeSeconds ?? (performance.now() - startTime) / 1000,
        )
        gl.uniform2f(
            resolutionLocation,
            canvasEl.value.width,
            canvasEl.value.height,
        )
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
    }

    const loop = () => {
        drawFrame()
        rafId = requestAnimationFrame(loop)
    }

    onMounted(() => {
        const canvas = canvasEl.value
        // No context yet: this only reads the tints, the shader gets them
        // once the program is linked
        applyTints()
        gl = canvas?.getContext('webgl', { alpha: true }) ?? null
        if (!canvas || !gl || !tints.value) {
            failed.value = true
            return
        }

        const vertex = compile(gl.VERTEX_SHADER, VERTEX_SHADER)
        const fragment = compile(gl.FRAGMENT_SHADER, FRAGMENT_SHADER)
        const program = gl.createProgram()
        if (!vertex || !fragment || !program) {
            failed.value = true
            return
        }
        gl.attachShader(program, vertex)
        gl.attachShader(program, fragment)
        gl.linkProgram(program)
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            failed.value = true
            return
        }
        gl.useProgram(program)

        // Fullscreen quad
        const buffer = gl.createBuffer()
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
        gl.bufferData(
            gl.ARRAY_BUFFER,
            new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
            gl.STATIC_DRAW,
        )
        const positionLocation = gl.getAttribLocation(program, 'a_position')
        gl.enableVertexAttribArray(positionLocation)
        gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0)

        timeLocation = gl.getUniformLocation(program, 'u_time')
        resolutionLocation = gl.getUniformLocation(program, 'u_resolution')
        centerLocation = gl.getUniformLocation(program, 'u_center')
        halfLocation = gl.getUniformLocation(program, 'u_half')
        spreadLocation = gl.getUniformLocation(program, 'u_spread')
        colorLocation = gl.getUniformLocation(program, 'u_color')
        color2Location = gl.getUniformLocation(program, 'u_color2')
        duoLocation = gl.getUniformLocation(program, 'u_duo')
        applyTints()

        resizeObserver = new ResizeObserver(resize)
        resizeObserver.observe(canvas)
        resize()
        // And again once the rest of the tree exists: the source is usually a
        // sibling rendered after this canvas, so the measure above ran while
        // the selector still matched nothing and silently fell back to the
        // parent box.
        void nextTick(resize)

        if (prefersReducedMotion()) {
            // Static frame: the color stays, the motion goes
            drawFrame(10)
            return
        }
        loop()
    })

    onBeforeUnmount(() => {
        cancelAnimationFrame(rafId)
        resizeObserver?.disconnect()
    })

    // Release the context (Chrome caps live WebGL contexts per page) only
    // after the compositor committed the removal: dropping the backing
    // buffer while the last frame is still on screen paints an undefined
    // (white) frame — a visible flash on the dark theme
    onUnmounted(() => {
        const context = gl
        gl = null
        setTimeout(() => {
            context?.getExtension('WEBGL_lose_context')?.loseContext()
        }, 150)
    })
</script>

<template>
    <div ref="rootEl" class="pk-aurora" aria-hidden="true" :style="tintStyle">
        <canvas v-if="!failed" ref="canvasEl" class="pk-aurora__canvas" />
        <div v-else class="pk-aurora__fallback" />
    </div>
</template>

<style lang="scss">
    .pk-aurora {
        position: absolute;
        inset: 0;

        // Where the tints start from when no `mainColor` is given, and what
        // the fallback draws when they could not be read
        color: var(--chat-page-accent, var(--color-brand));
        pointer-events: none;

        &__canvas {
            width: 100%;
            height: 100%;

            // No CSS filter on purpose: a filtered accelerated canvas gets
            // its own compositor surface, which Chrome can present
            // uninitialized (a white flash) when the layer is created or
            // torn down. The glow is smoothed in the shader instead.
        }

        // CSS blobs when WebGL is unavailable
        &__fallback {
            position: absolute;
            inset: 0;
            background:
                radial-gradient(
                    45% 65% at 32% 38%,
                    color-mix(
                        in srgb,
                        var(--pk-aurora-tint, currentcolor) 26%,
                        transparent
                    ),
                    transparent 70%
                ),
                radial-gradient(
                    40% 55% at 68% 62%,
                    color-mix(
                        in srgb,
                        var(--pk-aurora-tint-2, currentcolor) 16%,
                        transparent
                    ),
                    transparent 70%
                );
            filter: blur(48px);
            animation: pk-aurora-drift 14s var(--ease-in-out) infinite alternate;

            @media (prefers-reduced-motion: reduce) {
                animation: none;
            }
        }
    }

    @keyframes pk-aurora-drift {
        from {
            transform: rotate(-10deg) scale(1);
        }

        to {
            transform: rotate(10deg) scale(1.2);
        }
    }
</style>
